import NodeSelect from "@components/NodeSelect"
import { getNodeSelectOptions } from "@components/NodeSelect/helpers"
import { RunDto, RunResults } from "@custom-types/modelRun"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { SelectedNodes } from "@features/results/Result/Dashboard/DashboardBarChart"
import { useDashboardConfig } from "@features/results/Result/Dashboard/dashboardConfig"
import { DashboardBarChartDto } from "@features/results/Result/Dashboard/dashboardModel"
import ChartOptionsDropdown, {
  StackedBarChartOptions,
} from "@features/results/Result/MultiNodeBarChartContainer/ChartOptionsDropdown"
import MultiNodeBarChart from "@features/results/Result/MultiNodeBarChartContainer/MultiNodeBarChart"
import ShowChartAsTableButton from "@features/results/Result/ShowChartAsTableButton"
import ToggleNodeChips from "@features/results/Result/ToggleNodeChips"
import { Button, CircularProgress, Grid, Stack, SxProps, Typography } from "@mui/material"
import { GraphNodeData, GraphNodeEdgeData } from "@store/services/converter/types"
import { getNodeResultsEntries } from "@utils/run"
import { Chart } from "chart.js"
import { useEffect, useRef, useState } from "react"
import { areNodesUsingTheSameUnit } from "utils/node"

type MultiNodeBarChartContainerProps = {
  graphData: GraphNodeEdgeData
  nodesData: GraphNodeData[]
  runResults: RunResults | undefined
  isLoading: boolean
  runs: RunDto[]
  lag: number
  onlyGraph?: boolean
  chartOptions?: StackedBarChartOptions
  maintainAspectRatio?: boolean
  customLabels?: string[]
  legendPosition?: "top" | "bottom"
  containerSx?: SxProps
  chartHeight?: string // caps the canvas, needs maintainAspectRatio={false}
  chart?: DashboardBarChartDto
  selectedNodes?: SelectedNodes
  showAsTable?: boolean
  onShowAsTable?: (value: boolean) => void
  showLegend?: boolean
  hiddenDatasets?: string[]
  onLegendClick?: (datasetLabel: string) => void
  title: string
}

const MultiNodeBarChartContainer = (props: MultiNodeBarChartContainerProps) => {
  const runKeys = props.runs.map((run) => run.key)
  const chartRef = useRef<Chart<"bar"> | undefined>()
  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const [{ isFullscreen }] = useDashboardConfig()

  const [compareNodeSelectValue, setCompareNodeSelectValue] = useState<GroupNodeLabel | null>(null)
  const [selectedChipNodeLabels, setSelectedChipNodeLabels] = useState<string[]>([])

  const nodesLabels = props.nodesData.map(({ display }) => display.label)
  const visibleNodesData = props.nodesData.filter((nodeData) =>
    selectedChipNodeLabels.includes(nodeData.display.label),
  )
  const visibleNodeLabelsParsed = selectedChipNodeLabels.map(graphViewGroupNodeLabelToSampleNode)

  const chartEntries = getNodeResultsEntries(props.runResults)
  const chartEntriesFilteredByRuns = chartEntries.filter((entry) => runKeys.includes(entry.runKey))
  const chartEntryCompare = chartEntriesFilteredByRuns.find((entry) => {
    const optionSelected = Number(props.selectedNodes?.index)
    const chartDefinedCompareLabel =
      props.chart && !props.chart.isCompareResultsChart
        ? props.chart.NodeSelect?.[optionSelected]?.Compare ?? props.chart.Compare
        : null

    const label =
      chartDefinedCompareLabel ??
      graphViewGroupNodeLabelToSampleNode(compareNodeSelectValue?.label ?? "")
    const parsedLabel = graphViewGroupNodeLabelToSampleNode(label)
    const hasMatchingName = entry.nodeName === parsedLabel
    const isSelected = visibleNodeLabelsParsed.includes(parsedLabel)

    return hasMatchingName && isSelected
  })

  const visibleChartEntries = chartEntriesFilteredByRuns.filter((entry) => {
    const isChipSelected = visibleNodeLabelsParsed.includes(entry.nodeName)
    const sameAsCompareResult = chartEntryCompare
      ? chartEntryCompare.nodeName === entry.nodeName
      : false

    return isChipSelected && !sameAsCompareResult
  })
  const areNodesWithTheSameUnit = areNodesUsingTheSameUnit(visibleNodesData)

  const yAxisFormat = visibleNodesData?.[0]?.display?.["Unit Format"]

  useEffect(() => {
    setSelectedChipNodeLabels(nodesLabels)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [props.nodesData])

  const handleChipToggle = (nodeLabel: string) => {
    if (selectedChipNodeLabels.includes(nodeLabel)) {
      setSelectedChipNodeLabels(selectedChipNodeLabels.filter((label) => label !== nodeLabel))
    } else {
      setSelectedChipNodeLabels([...selectedChipNodeLabels, nodeLabel])
    }
  }

  if (!areNodesWithTheSameUnit) {
    return (
      <Grid container direction="column" gap={1}>
        <Grid item>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <ToggleNodeChips
              nodeLabels={nodesLabels}
              selectedNodeLabels={selectedChipNodeLabels}
              onToggle={handleChipToggle}
            />
          </Stack>
        </Grid>

        <Grid item sx={{ my: 6 }}>
          <Typography sx={{ display: "flex", justifyContent: "center" }}>
            Can't render the chart, as selected Nodes have different units. Disable some nodes in
            tags above
          </Typography>
        </Grid>
      </Grid>
    )
  }

  if (selectedChipNodeLabels.length === 0) {
    return (
      <Grid container direction="column" gap={1}>
        <Grid item>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <ToggleNodeChips
              nodeLabels={nodesLabels}
              selectedNodeLabels={selectedChipNodeLabels}
              onToggle={handleChipToggle}
            />
          </Stack>
        </Grid>

        <Grid item sx={{ my: 6 }}>
          <Typography sx={{ display: "flex", justifyContent: "center" }}>
            Please, re-select some nodes in tags above
          </Typography>
        </Grid>
      </Grid>
    )
  }

  const handleResetZoom = () => {
    if (!chartRef.current) return

    chartRef.current.resetZoom()
  }

  return (
    <Grid
      container
      direction="column"
      gap={1}
      sx={props.containerSx}
      key={String(isFullscreen)} // re render on isFullscreen change
    >
      {!props.onlyGraph && (
        <Grid item>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <ToggleNodeChips
              nodeLabels={nodesLabels}
              selectedNodeLabels={selectedChipNodeLabels}
              onToggle={handleChipToggle}
            />
          </Stack>
        </Grid>
      )}

      {!props.onlyGraph && (
        <Grid container item gap={2} alignItems="center">
          <Grid item>
            <NodeSelect
              label="Node comparison"
              graph={props.graphData}
              value={compareNodeSelectValue}
              onChange={setCompareNodeSelectValue}
              options={getNodeSelectOptions(props.nodesData)}
              groupBy={undefined} // disable grouping
              disableClearable
              sx={{ backgroundColor: "white", width: 300 }}
            />
          </Grid>
          <Grid item>
            <ChartOptionsDropdown />
          </Grid>
          <Grid item>
            <Button onClick={handleResetZoom} variant="contained">
              Reset zoom
            </Button>
          </Grid>
          {!props.showAsTable && (
            <Grid item>
              <DownloadChartImageDialog
                chartRef={chartContainerRef}
                defaultFileName="Stacked bar chart"
                runReference={runKeys.join(" vs. ")}
              />
            </Grid>
          )}

          <Grid item>
            <ShowChartAsTableButton
              showAsTable={props.showAsTable ?? false}
              onClick={() => props.onShowAsTable?.(!props.showAsTable)}
            />
          </Grid>
        </Grid>
      )}

      {props.isLoading ? (
        <Grid container justifyContent="center">
          <Grid item>
            <CircularProgress />
          </Grid>
        </Grid>
      ) : (
        <Grid container item direction="column" ref={chartContainerRef} sx={props.containerSx}>
          {!props.onlyGraph && (
            <Grid item sx={{ width: "100%" }}>
              <Typography variant="h6" textAlign="center">
                Stacked bar chart for <strong>{nodesLabels.join(", ")}</strong>
              </Typography>
            </Grid>
          )}
          <Grid
            item
            sx={{ ...props.containerSx, height: props.showAsTable ? undefined : props.chartHeight }}
          >
            <MultiNodeBarChart
              title={props.title}
              yAxisUnitFormat={yAxisFormat}
              compareToResult={chartEntryCompare?.results}
              nodeResults={visibleChartEntries.map((entry) => entry.results)}
              runs={props.runs}
              lag={props.lag}
              ref={chartRef}
              chartOptions={props.chartOptions}
              maintainAspectRatio={props.maintainAspectRatio}
              customLabels={props.customLabels}
              legendPosition={props.legendPosition}
              showAsTable={props.showAsTable}
              showLegend={props.showLegend}
              hiddenDatasets={props.hiddenDatasets}
              onLegendClick={props.onLegendClick}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}

export default MultiNodeBarChartContainer
